import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { COLLABORATORS, ConfigUtils, STRUCTURES } from '../lib/dashboard/config.js';
import CSVParser from '../lib/dashboard/csvParser.js';
import DataProcessor from '../lib/dashboard/dataProcessor.js';
import DateUtils from '../lib/dashboard/dateUtils.js';
import { exportDashboard } from '../lib/dashboard/pdfExport.js';
import Storage from '../lib/dashboard/storage.js';
import { STRUCT_CNT_IDS } from '../lib/dashboard/dashboardUtils.js';

const AppContext = createContext(null);

function injectManualCollaborators(manualMap) {
  Object.entries(manualMap).forEach(([name, struct]) => {
    const id = ConfigUtils.normalizeCollaboratorName(name);
    if (!COLLABORATORS[id]) {
      COLLABORATORS[id] = { id, name, structure: struct, phone: '', email: '', role: '' };
    }
    if (STRUCTURES[struct] && !STRUCTURES[struct].collaborators.includes(id)) {
      STRUCTURES[struct].collaborators.push(id);
    }
  });
}

export function AppProvider({ children }) {
  const [currentStructure, setCurrentStructure] = useState('LILLYBELLE');
  const [filteredData, setFilteredData] = useState([]);
  const [manualCollaborators, setManualCollaborators] = useState({});
  const [filterFrom, setFilterFrom] = useState(DateUtils.monthStart());
  const [filterTo, setFilterTo] = useState(DateUtils.today());
  const [filterCollab, setFilterCollab] = useState('');
  const [filterTeam, setFilterTeam] = useState('');
  const [filterActivity, setFilterActivity] = useState('');
  const [dataVersion, setDataVersion] = useState(0);
  const [warning, setWarning] = useState(null);
  const [unmatchedModal, setUnmatchedModal] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [lastCSV, setLastCSV] = useState({ content: null, structure: null });
  const [exportingPdf, setExportingPdf] = useState(false);

  const bumpData = () => setDataVersion((v) => v + 1);

  useEffect(() => {
    Storage.init();
    const saved = localStorage.getItem('manualCollaborators');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setManualCollaborators(parsed);
        injectManualCollaborators(parsed);
      } catch (e) {
        console.error('Erreur lecture collaborateurs manuels:', e);
      }
    }
  }, []);

  const structureCounts = useMemo(() => {
    void dataVersion;
    const allEntries = Storage.getAllEntries();
    const counts = { all: allEntries.length };
    Object.keys(STRUCTURES).forEach((structId) => {
      counts[structId] = allEntries.filter((e) => e.structure === structId).length;
    });
    return counts;
  }, [dataVersion]);

  const collaboratorOptions = useMemo(() => {
    void dataVersion;
    return [...new Set(Storage.getAllEntries().map((e) => e.collaboratorName))].sort();
  }, [dataVersion]);

  const activityOptions = useMemo(() => {
    void dataVersion;
    return [...new Set(Storage.getAllEntries().map((e) => e.activity))].sort();
  }, [dataVersion]);

  const teamOptions = useMemo(() => {
    let collabIds;
    if (currentStructure) {
      collabIds = STRUCTURES[currentStructure]?.collaborators || [];
    } else {
      collabIds = Object.keys(COLLABORATORS);
    }
    return [...new Set(
      collabIds.map((id) => COLLABORATORS[id]?.team).filter((t) => t && t.trim() !== '')
    )].sort();
  }, [currentStructure, dataVersion]);

  const applyFilters = useCallback(() => {
    if (!filterFrom || !filterTo) return;

    let entries = Storage.getEntriesByStructure(currentStructure || null, filterFrom, filterTo);

    if (filterTeam) {
      entries = entries.filter((e) => COLLABORATORS[e.collaborator]?.team === filterTeam);
    }
    if (filterCollab) {
      entries = entries.filter((e) => e.collaboratorName === filterCollab);
    }
    if (filterActivity) {
      entries = entries.filter((e) => e.activity === filterActivity);
    }

    setFilteredData(entries);
  }, [currentStructure, filterFrom, filterTo, filterCollab, filterTeam, filterActivity]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const selectStructure = (struct) => {
    setCurrentStructure(struct);
    setFilterTeam('');
  };

  const resetFilters = () => {
    setFilterFrom(DateUtils.monthStart());
    setFilterTo(DateUtils.today());
    setFilterCollab('');
    setFilterActivity('');
    setFilterTeam('');
    setCurrentStructure('LILLYBELLE');
  };

  const saveManualCollaborators = (map) => {
    localStorage.setItem('manualCollaborators', JSON.stringify(map));
    setManualCollaborators(map);
    injectManualCollaborators(map);
  };

  const reprocessLastCSV = useCallback((csv = lastCSV) => {
    if (!csv.content) return;
    const result = CSVParser.parse(csv.content, csv.structure);
    if (result.data.length > 0) {
      Storage.addEntries(result.data);
      bumpData();
      setWarning({
        type: 'success',
        message: `✅ Données mises à jour — ${result.data.length} lignes importées avec le nouveau collaborateur.`
      });
    }
    setLastCSV({ content: null, structure: null });
  }, [lastCSV]);

  const processCSVResult = (result, struct) => {
    const unmatched = result.errors.filter((e) => e.message.includes('non trouvé'));
    const otherErrors = result.errors.filter((e) => !e.message.includes('non trouvé'));
    const total = result.data.length + result.errors.length;

    if (result.data.length > 0) {
      Storage.addEntries(result.data);
      bumpData();
      console.log(`✅ ${result.data.length}/${total} lignes importées`);
    }

    if (result.errors.length > 0) {
      console.warn(`⚠️ ${result.errors.length} lignes rejetées sur ${total}:`);
      result.errors.forEach((e) => console.warn(`  Ligne ${e.row}: ${e.message}`));
    }

    if (unmatched.length > 0) {
      const names = [...new Set(
        unmatched.map((e) => e.message.match(/"([^"]+)"/)?.[1] || '').filter(Boolean)
      )];
      setUnmatchedModal(names);
    } else if (result.data.length > 0 && result.errors.length === 0) {
      setWarning({ type: 'success', message: `✅ ${result.data.length} lignes importées avec succès.` });
    } else if (result.data.length > 0 && otherErrors.length > 0) {
      setWarning({
        type: 'warn',
        message: `⚠️ ${result.data.length} lignes importées, ${otherErrors.length} rejetées (voir console F12).`
      });
    }
  };

  const loadCSV = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setLastCSV({ content, structure: currentStructure });
      processCSVResult(CSVParser.parse(content, currentStructure), currentStructure);
    };
    reader.readAsText(file);
  };

  const clearAllData = () => {
    if (window.confirm('Supprimer TOUTES les données importées ? Les données ne peuvent pas être récupérées.')) {
      localStorage.removeItem('timeTrackingData');
      localStorage.removeItem('manualCollaborators');
      setManualCollaborators({});
      Storage.init();
      bumpData();
      setFilteredData([]);
      console.log('✅ Données et collaborateurs manuels effacés');
    }
  };

  const saveAssignments = (assignments) => {
    const next = { ...manualCollaborators };
    let saved = 0;
    assignments.forEach(({ name, struct }) => {
      if (name && struct) {
        next[name] = struct;
        saved++;
      }
    });
    if (saved > 0) {
      saveManualCollaborators(next);
      setUnmatchedModal(null);
      reprocessLastCSV();
    } else {
      setUnmatchedModal(null);
    }
  };

  const addManualCollab = (name, struct) => {
    const next = { ...manualCollaborators, [name]: struct };
    saveManualCollaborators(next);
  };

  const updateManualCollab = (name, struct) => {
    const next = { ...manualCollaborators, [name]: struct };
    saveManualCollaborators(next);
    const id = ConfigUtils.normalizeCollaboratorName(name);
    if (COLLABORATORS[id]) COLLABORATORS[id].structure = struct;
  };

  const deleteManualCollab = (name) => {
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    const next = { ...manualCollaborators };
    delete next[name];
    saveManualCollaborators(next);
  };

  const handleExportPdf = async () => {
    if (!filteredData.length) {
      alert('Aucune donnée à exporter');
      return;
    }
    setExportingPdf(true);
    await exportDashboard(currentStructure, {
      onStart: () => {},
      onEnd: () => setExportingPdf(false)
    });
  };

  const hasDashboard = filteredData.length > 0;
  const structName = currentStructure ? STRUCTURES[currentStructure]?.name : 'Toutes structures';
  const pageSubtitle = filterFrom && filterTo
    ? `${DateUtils.formatDate(filterFrom, 'FR')} → ${DateUtils.formatDate(filterTo, 'FR')}`
    : '—';

  const stats = hasDashboard ? DataProcessor.getStats(filteredData) : null;
  const entriesForMissing = useMemo(() => {
    if (!hasDashboard || !filterFrom || !filterTo) return [];
    return Storage.getEntriesByStructure(currentStructure || null, filterFrom, filterTo);
  }, [hasDashboard, currentStructure, filterFrom, filterTo, dataVersion]);

  const value = {
    currentStructure,
    selectStructure,
    filteredData,
    hasDashboard,
    structName,
    pageSubtitle,
    stats,
    entriesForMissing,
    filterFrom,
    setFilterFrom,
    filterTo,
    setFilterTo,
    filterCollab,
    setFilterCollab,
    filterTeam,
    setFilterTeam,
    filterActivity,
    setFilterActivity,
    applyFilters,
    resetFilters,
    structureCounts,
    collaboratorOptions,
    activityOptions,
    teamOptions,
    loadCSV,
    clearAllData,
    warning,
    setWarning,
    unmatchedModal,
    setUnmatchedModal,
    saveAssignments,
    manageOpen,
    setManageOpen,
    manualCollaborators,
    addManualCollab,
    updateManualCollab,
    deleteManualCollab,
    handleExportPdf,
    exportingPdf,
    STRUCT_CNT_IDS
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}


